import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function ReportMonthlyAttendance() {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [teacherType, setTeacherType] = useState('ALL');
    const [teacherTypeList, setTeacherTypeList] = useState([]);
    const [formattedMonth, setFormattedMonth] = useState('');
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [schoolName, setSchoolName] = useState('ANANDA COLLEGE');

    const fetchTeacherTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher-types`);
            setTeacherTypeList(response.data || []);
        } catch (error) {
            console.error('Error fetching teacher types:', error);
        }
    }, []);

    const fetchMonthInfo = useCallback(async (year, month) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/month-info`, { params: { year, month } });
            setFormattedMonth(response.data.formattedMonth || '');
            setDaysInMonth(response.data.daysInMonth || 31);
            if (response.data.schoolName) setSchoolName(response.data.schoolName);
        } catch (error) {
            const date = new Date(year, month - 1, 1);
            setFormattedMonth(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
            setDaysInMonth(new Date(year, month, 0).getDate());
        }
    }, []);

    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        try {
            const year = selectedMonth.getFullYear();
            const month = selectedMonth.getMonth() + 1;

            const response = await axios.get(`${API_BASE_URL}/attendance/monthly`, {
                params: {
                    year,
                    month,
                    teacherType: teacherType !== 'ALL' ? teacherType : null,
                    page: 0,
                    pageSize: 50000,
                    sortField: 'teacherSignatureId',
                    sortOrder: 1
                }
            });

            setAttendanceData(response.data.data || []);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            setAttendanceData([]);
            const msg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Failed to load report data. Check that the API server is running.\n\nError: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, teacherType]);

    useEffect(() => {
        fetchTeacherTypes();
    }, [fetchTeacherTypes]);

    useEffect(() => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        fetchMonthInfo(year, month);
        fetchAttendanceData();
    }, [selectedMonth, teacherType, fetchMonthInfo, fetchAttendanceData]);

    const dayColumns = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const key = `day${String(d).padStart(2, '0')}`;
        dayColumns.push(
            <Column key={key} field={key} header={String(d).padStart(2, '0')}
                style={{ width: '40px', textAlign: 'center', padding: '4px' }}
                body={(rowData) => {
                    const val = rowData[key];
                    if (!val || val === '-') return <span style={{ color: '#ccc' }}>-</span>;
                    const colors = { P: '#28a745', A: '#dc3545', L: '#6f42c1' };
                    return <span style={{ color: colors[val] || '#333', fontWeight: 'normal', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{val}</span>;
                }}
            />
        );
    }

    const getReportTitle = () => {
        const monthStr = formattedMonth || selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        return `TEACHERS MONTHLY ATTENDANCE REPORT - ${schoolName} - ${monthStr}`;
    };

    const exportToCSV = () => {
        if (attendanceData.length === 0) return alert('No data to export');

        const headers = ['#', 'TID', 'Name'];
        for (let d = 1; d <= daysInMonth; d++) headers.push(String(d).padStart(2, '0'));

        const csvRows = [headers.join(',')];
        attendanceData.forEach((row, idx) => {
            const values = [
                idx + 1,
                `"${(row.teacherSignatureId || row.tid || '').toString().replace(/"/g, '""')}"`,
                `"${(row.teacherName || '').replace(/"/g, '""')}"`
            ];
            for (let d = 1; d <= daysInMonth; d++) {
                values.push(row[`day${String(d).padStart(2, '0')}`] || '-');
            }
            csvRows.push(values.join(','));
        });

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const year = selectedMonth.getFullYear();
        const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
        link.download = `Monthly_Attendance_${year}_${month}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const exportToPDF = () => {
        if (attendanceData.length === 0) return alert('No data to export');

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        doc.setFontSize(14);
        doc.text(getReportTitle(), 14, 15);

        const headers = ['#', 'TID', 'Name'];
        for (let d = 1; d <= daysInMonth; d++) headers.push(String(d).padStart(2, '0'));

        const body = attendanceData.map((row, idx) => {
            const r = [idx + 1, row.teacherSignatureId || row.tid, row.teacherName];
            for (let d = 1; d <= daysInMonth; d++) {
                r.push(row[`day${String(d).padStart(2, '0')}`] || '-');
            }
            return r;
        });

        autoTable(doc, {
            head: [headers],
            body: body,
            startY: 22,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1.5, lineColor: [200, 200, 200], lineWidth: 0.1 },
            headStyles: { fillColor: [30, 58, 95], fontSize: 7, lineColor: [200, 200, 200], lineWidth: 0.1 },
            columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 15 }, 2: { cellWidth: 40 } }
        });

        const year = selectedMonth.getFullYear();
        const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
        doc.save(`Monthly_Attendance_${year}_${month}.pdf`);
    };

    const teacherTypeOptions = teacherTypeList.length > 0
        ? teacherTypeList.map(t => ({ label: t.label || t.type || t, value: t.value || t.type || t }))
        : [{ label: 'ALL', value: 'ALL' }];

    return (
        <div style={{ padding: '1.5rem' }}>
            <h2 style={{ textAlign: 'center', color: '#1e3a5f', marginBottom: '1rem' }}>
                {getReportTitle()}
            </h2>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Select Month</label>
                    <Calendar value={selectedMonth} onChange={(e) => setSelectedMonth(e.value)} view="month" dateFormat="yy-mm" showIcon />
                </div>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Teacher Type</label>
                    <Dropdown value={teacherType} options={teacherTypeOptions} onChange={(e) => setTeacherType(e.value)} style={{ minWidth: '150px' }} />
                </div>
                <Button label="Search" icon="pi pi-search" onClick={fetchAttendanceData} className="p-button-primary" />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                    <Button label="CSV" icon="pi pi-file" onClick={exportToCSV} className="p-button-info" />
                    <Button label="PDF" icon="pi pi-file-pdf" onClick={exportToPDF} className="p-button-danger" />
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}><ProgressSpinner /></div>
            ) : (
                <DataTable value={attendanceData} scrollable scrollHeight="calc(100vh - 250px)"
                    emptyMessage="No attendance data found for this period."
                    size="small" stripedRows>
                    <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: '40px', textAlign: 'center' }} frozen />
                    <Column field="teacherSignatureId" header="TID" style={{ width: '70px' }} frozen
                        body={(row) => row.teacherSignatureId || row.tid} />
                    <Column field="teacherName" header="Name" style={{ minWidth: '180px' }} frozen />
                    {dayColumns}
                </DataTable>
            )}

            <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                Total Records: {attendanceData.length}
            </div>
        </div>
    );
}

export default ReportMonthlyAttendance;
